import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { Alert, TimeRange } from '../api/types'
import { Empty, ErrorBanner, Loading } from '../components/Common'
import { StatusChip } from '../components/StatusChip'
import { TimeRangePicker } from '../components/TimeRangePicker'
import { usePolling } from '../hooks/usePolling'
import { usePreference } from '../hooks/usePreferences'
import { formatDuration, timeAgo } from '../theme/charts'

export function Alerts() {
  const [range, setRange] = usePreference<TimeRange>('range.alertHistory', 'week')
  const active = usePolling<Alert[]>(() => api.alerts(), 20_000)
  const history = usePolling<Alert[]>(() => api.alertHistory(range), 120_000, [range])

  const firing = (active.data ?? []).filter((a) => a.state === 'firing')
  const pending = (active.data ?? []).filter((a) => a.state === 'pending')

  return (
    <div className="stack">
      <div>
        <h1 className="page-title">Alerts</h1>
        <p className="page-subtitle">
          A rule fires only once its condition has held for the whole
          <code> for </code> window, and clears only after it has been false for
          the <code> resolve_after </code> window. That is what keeps a value
          sitting on its threshold from producing a stream of email.
        </p>
      </div>

      <ErrorBanner error={active.error} />

      <section>
        <h2 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>
          Firing ({firing.length})
        </h2>
        {active.loading && !active.data
          ? <Loading what="alerts" />
          : firing.length === 0
            ? <Empty>Nothing is firing. </Empty>
            : <AlertTable alerts={firing} showSince />}
      </section>

      {pending.length > 0 && (
        <section>
          <h2 className="card-title" style={{ marginBottom: 'var(--space-2)' }}>
            Pending ({pending.length})
          </h2>
          <p className="page-subtitle">
            The condition is currently true but has not held long enough to fire.
            No email has been sent for these.
          </p>
          <AlertTable alerts={pending} showSince />
        </section>
      )}

      <section>
        <div className="toolbar">
          <h2 className="card-title" style={{ margin: 0 }}>History</h2>
          <TimeRangePicker value={range} onChange={setRange}
                           options={['day', 'week', 'month', 'quarter']} />
        </div>
        {history.loading && !history.data
          ? <Loading what="history" />
          : (history.data ?? []).length === 0
            ? <Empty>No alerts recorded in this range.</Empty>
            : <AlertTable alerts={history.data ?? []} showDuration />}
      </section>
    </div>
  )
}

/**
 * Absolute date and time.
 *
 * <p>The live tables use relative ages ("6m ago"), which is what you want while
 * something is happening. History needs the wall-clock instant: correlating an
 * alert with a deploy, a log line or a customer report is impossible from a
 * relative age alone.
 */
function stamp(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return d.toLocaleString([], {
    year: '2-digit', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  })
}

function AlertTable({ alerts, showSince, showDuration }: {
  alerts: Alert[]
  showSince?: boolean
  showDuration?: boolean
}) {
  return (
    <div className="card">
      <div className="card-body" style={{ padding: 0 }}>
        <table className="table">
          <thead>
            <tr>
              <th>Severity</th>
              <th>Target</th>
              <th>Summary</th>
              <th className="num">Value</th>
              <th>Rule</th>
              {showSince && <th className="num">Since</th>}
              {showDuration && <th>Started</th>}
              {showDuration && <th>Ended</th>}
              {showDuration && <th className="num">Duration</th>}
              {showDuration && <th>State</th>}
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <tr key={a.id}>
                <td><StatusChip status={a.severity} /></td>
                <td>
                  <Link to={`/targets/${encodeURIComponent(a.targetId)}`}>{a.targetName}</Link>
                </td>
                <td>{a.summary}</td>
                <td className="num">
                  {a.lastValue !== undefined && a.lastValue !== null
                    ? a.lastValue.toFixed(2)
                    : '—'}
                  {a.threshold !== undefined && a.threshold !== null && (
                    <span className="muted"> / {a.threshold}</span>
                  )}
                </td>
                <td className="key">
                  {a.ruleId}
                  {a.label ? <span className="muted"> [{a.label}]</span> : null}
                </td>
                {showSince && <td className="num muted">{timeAgo(a.firedAt ?? a.startedAt)}</td>}
                {showDuration && <td className="nowrap tabular">{stamp(a.firedAt ?? a.startedAt)}</td>}
                {showDuration && (
                  <td className="nowrap tabular">
                    {a.resolvedAt
                      ? stamp(a.resolvedAt)
                      : <span className="muted">still open</span>}
                  </td>
                )}
                {showDuration && (
                  // Lifetime of the alert, not of the firing period: an alert
                  // that resolved before its "for" window elapsed still existed
                  // for a measurable time, and reporting that as "never fired"
                  // contradicted the Started/Ended columns beside it.
                  <td className="num muted">
                    {formatDuration(
                      ((a.resolvedAt ? new Date(a.resolvedAt).getTime() : Date.now())
                        - new Date(a.startedAt).getTime()) / 1000)}
                  </td>
                )}
                {showDuration && (
                  <td className={a.state === 'resolved' ? 'muted' : ''}>
                    {a.state}
                    {!a.firedAt && (
                      // Worth stating plainly: it never reached the threshold
                      // duration, so nobody was emailed about it.
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-muted)' }}>
                        never fired
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
